import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import RADApplication from '@/Management/RADApplication';
import ImageHandler from '@/Utility/ImageHandler';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import Utilities from '@/Utility/Utilities';
import {IImageViewModelConstructorParameter} from "#/DefaultViewExtensionInterfaces";

/**
* Das ViewModel für das Image
* @param {object} params - Ein Objekt mit Optionen für das Control
* @constructor
*/
export default class ImageViewModel extends ControlViewModel {
    /**
    * Gibt an, ob das Bild immer sichtbar ist (auch wenn z.B. die Pane collapsed ist)
    */
    public isAlwaysVisible: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob die Quelle bereits konvertiert ist
     */
    public isConvertedSource: boolean;

    /**
     * Gibt an, ob die Quelle aus Binärdaten besteht
     */
    public isBinary: boolean;

    /**
     * Gibt an, ob ein Parameter zur Verhinderung des Caching-Mechanismus generiert werden soll
     */
    public noCache: boolean;

    /**
     * Die Breite des Bildes
     */
    public width: KnockoutObservable<string | number>;

    /**
     * Die Höhe des Bildes
     */
    public height: KnockoutObservable<string | number>;

    /**
     * Die Quelle des Bildes
     */
    public source: KnockoutObservable<string>;

    /**
     * Gibt an, ob es sich bei dem Bild um einen Dateityp handelt
     */
    public isFileType: KnockoutObservable<boolean>;

    /**
     * Die konvertierte Quelle des Bildes
     */
    public convertedSource: KnockoutObservable<string>;

    /**
     * Bei SVG-Bildern wird hier das XML gespeichert 
     */
    public svgSource: KnockoutObservable<string>;

    /**
     * Der Alternativtext für das Bild
     */
    public alternativeText: KnockoutObservable<string>;

    /**
     * Zusätzlich anwendbare Styles
     */
    public additionalStyle: KnockoutObservable<string>;

    /**
     * Enable html in tooltip
     */
    public tooltipHtmlEnabled: boolean;

    public constructor(params: IImageViewModelConstructorParameter) {
        super(params);

        this.isAlwaysVisible = this.observableOrDefault(params.isAlwaysVisible, false);
        this.isConvertedSource = this.paramOrDefault(params.isConvertedSource, false);
        this.isBinary = this.paramOrDefault(params.isBinary, false);
        this.noCache = this.paramOrDefault(params.noCache, false);
        this.width = this.observableOrDefault(params.width, null);
        this.height = this.observableOrDefault(params.height, null);
        this.source = this.observableOrDefault(params.source, null);
        this.isFileType = this.observableOrDefault(params.isFileType, false);
        this.convertedSource = this.observableOrDefault('');
        this.svgSource = this.observableOrDefault(params.svgSource, null);
        this.alternativeText = this.isLabelViewModel(params.alternativeText) 
            ? params.alternativeText.caption 
            : this.observableOrDefault(params.alternativeText);
        
        this.additionalStyle = this.observableOrDefault('');
        this.tooltipHtmlEnabled = this.paramOrDefault(params.tooltipHtmlEnabled, false);

        // Sollte sich die Quelle des Bildes ändern, wird geprüft ob ein Sprite für dieses Bild existiert
        this.defineSubscription(this.source,(source) => {
            const templateStyle = RADApplication.getInstance().templateStyle();
            const width = this.width();
            const height = this.height();
            const isFileType = this.isFileType();

            if (this.isBinary === true) {
                if (source && !source.startsWith("data:") && !source.startsWith("blob:"))
                    source = 'data:image/png;base64,' + source;
                this.convertedSource(source);
            } else if (this.isConvertedSource === true) {
                if (source && this.noCache === true) {
                    source += source.indexOf('?') > -1 ? '&' : '?';
                    source += 'temp=' + new Date().getTime();
                }

                this.convertedSource(source);
            } else if (templateStyle && source) {
                const params = ImageHandler.getParameters(source, width, height, isFileType);

                $.when(params).then((result) => {
                    if (this.isDisposed)
                        return;

                    this.convertedSource(null);
                    this.svgSource(null);

                    if (!Utilities.isNullOrUndefined(result.source))
                        this.convertedSource(result.source);
                    else if (!Utilities.isNullOrUndefined(result.svgSource))
                        this.svgSource(result.svgSource);
                    this.additionalStyle(result.style);
                });
            }
        })(this.source());
    }
    
    private isLabelViewModel(value: any): value is LabelViewModel {
        return value instanceof LabelViewModel || value?.hasOwnProperty('caption') ;
    }
}

ComponentManager.setViewModel('ImageViewModel', ImageViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');